import HUI from "@henge-inc/ui/v1_2";
import gsap from "gsap";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flex, Text, Box } from "@radix-ui/themes";
import {
  BUSINESS_CARD_DEFAULT_HEIGHT,
  BUSINESS_CARD_DEFAULT_MIN_HEIGHT,
  BUSINESS_CARD_DEFAULT_MIN_WIDTH,
  BUSINESS_CARD_DEFAULT_WIDTH,
  MAX_DEGREE,
} from "./business-card-constants";
import { useOnClickOutside } from "usehooks-ts";
import { NameLogo } from "@/components/logo/NameLogo";
import { Hero } from "@/components/businessCard/Hero";
import { Skills } from "@/components/businessCard/Skills";
import { Works } from "@/components/businessCard/Works";
import { Projects } from "@/components/businessCard/Projects";
import { Qualifications } from "@/components/businessCard/Qualifications";
import { Footer } from "@/components/businessCard/Footer";
import { useRouter } from "next/router";

const ascii = `
  ____   ____  ____    ____       ____   ___   ____    ____  ____  ____ 
 |    | /    T|    \\  /    T     |    | /   \\ |    \\  /    Tl    j|    \\ 
 l__  |Y  o  ||  _  YY   __j     l__  |Y     Y|  _  YY   __j |  T |  _  Y 
 __j  ||     ||  |  ||  T  |     __j  ||  O  ||  |  ||  T  | |  | |  |  | 
/  |  ||  _  ||  |  ||  l_ |    /  |  ||     ||  |  ||  l_ | |  | |  |  | 
\\  \`  ||  |  ||  |  ||     |    \\  \`  |l     !|  |  ||     | j  l |  |  | 
 \\____jl__j__jl__j__jl___,_j     \\____j \\___/ l__j__jl___,_j|____jl__j__j 
`;

type AnimationState = "idle" | "flip";

interface BusinessCardProps {
  flipped: boolean;
}

export const BusinessCard = React.forwardRef<HTMLDivElement, BusinessCardProps>(
  ({ flipped }, ref) => {
    const router = useRouter();

    const businessCardRef = HUI.useForwardedRef<HTMLDivElement>(ref);

    // OUTER: tilt/scale only
    const tiltRef = useRef<HTMLDivElement>(null!);
    // INNER: flip only
    const flipRef = useRef<HTMLDivElement>(null!);

    const [hover, setHover] = useState(false);

    /* ---------------- scale handling (no transform conflict) ---------------- */
    const [scale, setScale] = useState(1);
    useEffect(() => {
      const el = businessCardRef.current;
      if (!el) return;

      const observer = new ResizeObserver(() => {
        const newScale =
          el.getBoundingClientRect().width / BUSINESS_CARD_DEFAULT_WIDTH;
        setScale(newScale);
      });

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    /* Apply scale as CSS variable on tiltRef */
    useEffect(() => {
      tiltRef.current?.style.setProperty("--scale", String(scale));
    }, [scale]);

    /* ---------------- device hints ---------------- */
    const isCoarsePointer = useMemo(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
    }, []);

    /* ---------------- Tilt motion (rAF commit, CSS vars only) ---------------- */
    const rafId = useRef<number | null>(null);
    const pending = useRef({ tx: 0, ty: 0, a: "0deg" });

    const cancelTiltRaf = () => {
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };

    const resetTilt = () => {
      cancelTiltRaf();
      pending.current.tx = 0;
      pending.current.ty = 0;
      pending.current.a = "0deg";

      const w = tiltRef.current;
      if (!w) return;
      w.style.setProperty("--tilt-x", "0");
      w.style.setProperty("--tilt-y", "0");
      w.style.setProperty("--tilt-angle", "0deg");
    };

    const commitTilt = () => {
      rafId.current = null;
      const w = tiltRef.current;
      if (!w) return;
      w.style.setProperty("--tilt-x", String(pending.current.tx));
      w.style.setProperty("--tilt-y", String(pending.current.ty));
      w.style.setProperty("--tilt-angle", pending.current.a);
    };

    const pointerMoveHandler = (e: React.PointerEvent) => {
      // 모바일에서는 tilt 비활성화(원하면 여기서 완화/변경 가능)
      if (isCoarsePointer) return;
      // 플립 중엔 tilt 업데이트 중지
      if (animationStateRef.current !== "idle") return;

      const wrapper = tiltRef.current;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * -2 + 1;

      const angle = Math.sqrt(x * x + y * y) * MAX_DEGREE;

      pending.current.tx = -y;
      pending.current.ty = -x;
      pending.current.a = `${angle}deg`;

      if (rafId.current == null) {
        rafId.current = requestAnimationFrame(commitTilt);
      }
    };

    const pointerEnterHandler = () => setHover(true);

    const pointerLeaveHandler = () => {
      resetTilt();
      setHover(false);
    };

    useOnClickOutside(tiltRef, () => {
      resetTilt();
      setHover(false);
    });

    useEffect(() => {
      return () => {
        if (rafId.current != null) cancelAnimationFrame(rafId.current);
      };
    }, []);

    /* ---------------- Flip animation (transform only) ---------------- */
    const flipTl = useRef<gsap.core.Timeline>(gsap.timeline({ paused: true }));
    const [animationState, setAnimationState] =
      useState<AnimationState>("idle");
    const animationStateRef = useRef<AnimationState>("idle");
    useEffect(() => {
      animationStateRef.current = animationState;
    }, [animationState]);

    const didSyncInitialFlip = useRef(false);

    useEffect(() => {
      const el = flipRef.current;
      if (!el) return;

      if (!didSyncInitialFlip.current) {
        didSyncInitialFlip.current = true;
        gsap.set(el, { rotateY: flipped ? 180 : 0 });
        return;
      }

      setAnimationState("flip");

      flipTl.current
        .clear()
        .to(el, {
          rotateY: flipped ? 180 : 0,
          ease: "easeIn",
          duration: 0.8,
        })
        .eventCallback("onComplete", () => {
          setAnimationState("idle");
        })
        .restart();
    }, [flipped]);

    /* ---------------- click -> flip toggle ---------------- */
    const start = useRef<number | null>(null);

    const onPointerDown = () => {
      start.current = Date.now();
    };

    const onPointerUp = () => {
      if (!start.current) return;
      if (Date.now() - start.current < 400) onClick();
      start.current = null;
    };

    const onClick = () => {
      if (animationStateRef.current !== "idle") return;
      router.push("/portfolio", undefined, { scroll: false });
    };

    /* ---------------- Render ---------------- */
    return (
      <div
        ref={businessCardRef}
        className={
          "flex justify-center items-center w-full h-full transition-all"
        }
        style={{
          aspectRatio: flipped ? undefined : "16 / 9",
          minWidth: `${BUSINESS_CARD_DEFAULT_MIN_WIDTH}px`,
          minHeight: `${BUSINESS_CARD_DEFAULT_MIN_HEIGHT}px`,
          perspective: flipped ? undefined : "1024px",
        }}
      >
        <div
          className={"flex justify-center items-center transition-all"}
          style={{
            aspectRatio: flipped ? undefined : "16 / 9",
            width: flipped ? "100%" : `${BUSINESS_CARD_DEFAULT_WIDTH}px`,
            height: flipped ? "100%" : `${BUSINESS_CARD_DEFAULT_HEIGHT}px`,
          }}
        >
          {/* OUTER: scale + tilt only */}
          <div
            ref={tiltRef}
            className={"relative w-full h-full"}
            style={{
              borderRadius: "4px",
              transformStyle: flipped ? "flat" : "preserve-3d",
              transform: flipped
                ? undefined
                : `perspective(512px)
              scale(calc(var(--scale, 1) * ${hover ? 1.05 : 1}))
              rotate3d(var(--tilt-x, 0), var(--tilt-y, 0), 0, var(--tilt-angle, 0deg))
              translateZ(0)`,
              transition: "transform 0.3s var(--ease-in-out)",
            }}
          >
            {/* INNER: flip only */}
            <div
              ref={flipRef}
              className={"absolute inset-0"}
              style={{
                width: "100%",
                height: "100%",
                transformStyle: "preserve-3d",
                transform: "rotateY(0deg)",
                overflow: flipped ? "visible" : "hidden",
              }}
            >
              {/* FRONT */}
              <div
                className={
                  "business-card-front business-card-paper select-none cursor-pointer absolute inset-0 flex flex-col flex-nowrap justify-between items-start gap-4 transition-opacity"
                }
                style={{
                  opacity: flipped ? 0 : 1,
                  borderRadius: "inherit",
                  boxShadow: isCoarsePointer
                    ? "0 6px 18px var(--gray50)"
                    : "calc(var(--tilt-y, 0) * -6px) calc(var(--tilt-x, 0) * 6px) 8px var(--gray200)",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "rotateY(0deg)",
                  transitionDuration: flipped ? "0.2s" : "1s",
                }}
                onPointerEnter={pointerEnterHandler}
                onPointerMove={pointerMoveHandler}
                onPointerLeave={pointerLeaveHandler}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
              >
                <Flex
                  direction={"column"}
                  justify={"between"}
                  align={"center"}
                  className={"w-full h-full"}
                >
                  <Box className={"w-full p-2"}>
                    <NameLogo />
                  </Box>
                  <Flex justify={"between"} className={"w-full h-14 px-4 pb-4"}>
                    <Text
                      size={"2"}
                      weight={"medium"}
                      className={"text-[var(--tomato-9)]"}
                    >
                      Web ·
                      <br />
                      Frontend
                    </Text>
                    <Text
                      size={"3"}
                      className="material-symbols-outlined text-[var(--tomato-9)]"
                      style={{
                        fontSize: "1.75rem",
                        fontWeight: "300",
                        transform: "rotate(45deg)",
                      }}
                    >
                      arrow_forward
                    </Text>
                  </Flex>
                </Flex>
              </div>

              {/* BACK */}
              <div
                className={
                  "business-card-back flex flex-col flex-nowrap justify-between items-start gap-4 transition-opacity"
                }
                style={{
                  opacity: flipped ? 1 : 0,
                  // borderRadius: "inherit",
                  // boxShadow: flipped
                  //   ? undefined
                  //   : isCoarsePointer
                  //     ? "0 6px 18px var(--gray50)"
                  //     : "calc(var(--tilt-y, 0) * -6px) calc(var(--tilt-x, 0) * 6px) 8px var(--gray100)",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  transitionDuration: flipped ? "1s" : "0s",
                }}
              >
                <Flex
                  direction={"column"}
                  justify={"start"}
                  align={"center"}
                  className={"w-full h-full px-6 bg-[var(--sand-2)]"}
                >
                  <Hero />
                  <Skills />
                  <Works />
                  <Projects />
                  <Qualifications />
                  <Footer />
                </Flex>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

BusinessCard.displayName = "BusinessCard";
